import { useEffect, useState } from "react";
import { toast } from "sonner";
import { ChevronUp, ChevronDown, ChevronLeft, ChevronRight, Upload, RotateCcw, Save, Eye, EyeOff } from "lucide-react";
import { api, errMsg } from "../../lib/api";
import { Spinner } from "../../components/common";
import { CardVisual, DEFAULT_DESIGN } from "../Cards";

const SAMPLE_CARD = { type: "virtual", last4: "4242", holder_name: "CLIENT NAME", exp_month: 12, exp_year: 2030 };
const ELEMENTS = [
  { key: "brand", label: "Logo" },
  { key: "cardType", label: "Card type label", textField: "text", textPlaceholder: "VIRTUAL (auto)" },
  { key: "chip", label: "Chip" },
  { key: "contactless", label: "Contactless (WiFi)" },
  { key: "number", label: "Card number", color: true },
  { key: "holder", label: "Holder name", color: true },
  { key: "network", label: "DEBIT / VISA", labelField: "label", brandField: "brand" },
];

export default function CardDesignTab() {
  const [d, setD] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    api.get("/admin/card-design").then((r) => setD({ ...DEFAULT_DESIGN, ...r.data })).catch((e) => toast.error(errMsg(e))).finally(() => setLoading(false));
  }, []);

  const setTop = (k, v) => setD((p) => ({ ...p, [k]: v }));
  const setEl = (key, patch) => setD((p) => ({ ...p, [key]: { ...p[key], ...patch } }));
  const nudge = (key, axis, delta) => setEl(key, { [axis]: ((d[key]?.[axis]) || 0) + delta });

  const onLogo = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (file.size > 400 * 1024) { toast.error("Logo too large (max 400 KB)"); return; }
    const reader = new FileReader();
    reader.onload = () => setTop("logo_data", reader.result);
    reader.readAsDataURL(file);
  };

  const save = async () => {
    setSaving(true);
    try { const r = await api.put("/admin/card-design", d); setD({ ...DEFAULT_DESIGN, ...r.data }); toast.success("Card design saved for all clients"); }
    catch (e) { toast.error(errMsg(e)); }
    finally { setSaving(false); }
  };

  const reset = () => setD({ ...DEFAULT_DESIGN });

  if (loading || !d) return <div className="py-16 flex justify-center"><Spinner className="w-6 h-6" /></div>;

  return (
    <div className="grid lg:grid-cols-2 gap-8" data-testid="card-design-tab">
      {/* Live preview */}
      <div className="lg:sticky lg:top-4 self-start">
        <div className="text-xs font-semibold text-muted-foreground uppercase tracking-wide mb-3">Live preview — this is exactly what every client sees</div>
        <div className="max-w-sm">
          <CardVisual card={SAMPLE_CARD} frozen={false} sensitive={null} flipped={false} onFlip={() => {}} design={d} />
        </div>
        <div className="flex gap-3 mt-5">
          <button data-testid="card-design-save" onClick={save} disabled={saving}
            className="flex-1 h-12 rounded-2xl bg-primary text-primary-foreground font-semibold flex items-center justify-center gap-2 disabled:opacity-60">
            {saving ? <Spinner className="w-5 h-5" /> : <><Save size={17} /> Save for all clients</>}
          </button>
          <button data-testid="card-design-reset" onClick={reset}
            className="h-12 px-4 rounded-2xl bg-secondary font-semibold flex items-center gap-2"><RotateCcw size={16} /> Reset</button>
        </div>
      </div>

      {/* Controls */}
      <div className="space-y-6">
        <Section title="Background & glow">
          <div className="grid grid-cols-2 gap-3">
            <ColorField label="Gradient top" value={d.bg_from} onChange={(v) => setTop("bg_from", v)} testid="bg-from" />
            <ColorField label="Gradient middle" value={d.bg_via} onChange={(v) => setTop("bg_via", v)} testid="bg-via" />
            <ColorField label="Gradient bottom" value={d.bg_to} onChange={(v) => setTop("bg_to", v)} testid="bg-to" />
            <ColorField label="Glow color" value={d.glow} onChange={(v) => setTop("glow", v)} testid="glow" />
          </div>
        </Section>

        <Section title="Logo">
          <div className="flex items-center gap-3">
            <label className="h-11 px-4 rounded-xl bg-secondary font-semibold text-sm flex items-center gap-2 cursor-pointer" data-testid="logo-upload-label">
              <Upload size={16} /> Upload logo
              <input type="file" accept="image/*" className="hidden" onChange={onLogo} data-testid="logo-upload-input" />
            </label>
            {d.logo_data ? (
              <button onClick={() => setTop("logo_data", "")} className="text-sm text-destructive font-semibold" data-testid="logo-remove">Remove (use default wordmark)</button>
            ) : <span className="text-xs text-muted-foreground">Default: Jack Cash® wordmark</span>}
          </div>
        </Section>

        <Section title="Elements — show, move & edit">
          <div className="space-y-3">
            {ELEMENTS.map((el) => (
              <div key={el.key} className="rounded-xl border border-border p-3" data-testid={`el-row-${el.key}`}>
                <div className="flex items-center justify-between">
                  <span className="text-sm font-semibold">{el.label}</span>
                  <button onClick={() => setEl(el.key, { show: !d[el.key].show })}
                    data-testid={`el-toggle-${el.key}`}
                    className={`flex items-center gap-1.5 text-xs font-semibold px-2.5 py-1 rounded-lg ${d[el.key].show ? "bg-success/10 text-success" : "bg-muted text-muted-foreground"}`}>
                    {d[el.key].show ? <Eye size={13} /> : <EyeOff size={13} />}{d[el.key].show ? "Shown" : "Hidden"}
                  </button>
                </div>
                {d[el.key].show && (
                  <div className="flex flex-wrap items-center gap-4 mt-3">
                    {/* position nudger */}
                    <div className="flex items-center gap-1">
                      <span className="text-[11px] text-muted-foreground mr-1">Move</span>
                      <NudgeBtn onClick={() => nudge(el.key, "x", -4)} testid={`nudge-${el.key}-left`}><ChevronLeft size={15} /></NudgeBtn>
                      <div className="flex flex-col">
                        <NudgeBtn onClick={() => nudge(el.key, "y", -4)} testid={`nudge-${el.key}-up`}><ChevronUp size={15} /></NudgeBtn>
                        <NudgeBtn onClick={() => nudge(el.key, "y", 4)} testid={`nudge-${el.key}-down`}><ChevronDown size={15} /></NudgeBtn>
                      </div>
                      <NudgeBtn onClick={() => nudge(el.key, "x", 4)} testid={`nudge-${el.key}-right`}><ChevronRight size={15} /></NudgeBtn>
                      <span className="text-[10px] text-muted-foreground ml-1 tabular-nums">{d[el.key].x || 0},{d[el.key].y || 0}</span>
                    </div>
                    {el.textField && (
                      <input value={d[el.key][el.textField] || ""} onChange={(e) => setEl(el.key, { [el.textField]: e.target.value })}
                        placeholder={el.textPlaceholder} data-testid={`text-${el.key}`}
                        className="flex-1 min-w-[120px] h-9 px-3 rounded-lg border border-border bg-background text-sm" />
                    )}
                    {el.labelField && (
                      <input value={d[el.key][el.labelField] || ""} onChange={(e) => setEl(el.key, { [el.labelField]: e.target.value })}
                        placeholder="DEBIT" data-testid={`text-${el.key}-label`}
                        className="w-24 h-9 px-3 rounded-lg border border-border bg-background text-sm" />
                    )}
                    {el.brandField && (
                      <input value={d[el.key][el.brandField] || ""} onChange={(e) => setEl(el.key, { [el.brandField]: e.target.value })}
                        placeholder="VISA" data-testid={`text-${el.key}-brand`}
                        className="w-24 h-9 px-3 rounded-lg border border-border bg-background text-sm" />
                    )}
                    {el.color && (
                      <ColorField label="Color" value={d[el.key].color || "#ffffff"} onChange={(v) => setEl(el.key, { color: v })} testid={`color-${el.key}`} inline />
                    )}
                  </div>
                )}
              </div>
            ))}
          </div>
        </Section>
      </div>
    </div>
  );
}

function Section({ title, children }) {
  return (
    <div className="rounded-2xl bg-card border border-border p-5">
      <h3 className="font-display font-semibold mb-4">{title}</h3>
      {children}
    </div>
  );
}

function ColorField({ label, value, onChange, testid, inline }) {
  return (
    <label className={`flex items-center gap-2 ${inline ? "" : "flex-col items-start"}`}>
      <span className="text-[11px] text-muted-foreground">{label}</span>
      <div className="flex items-center gap-2">
        <input type="color" value={value} onChange={(e) => onChange(e.target.value)} data-testid={`${testid}-picker`}
          className="w-9 h-9 rounded-lg border border-border bg-transparent cursor-pointer" />
        <input value={value} onChange={(e) => onChange(e.target.value)} data-testid={`${testid}-hex`}
          className="w-24 h-9 px-2 rounded-lg border border-border bg-background text-xs font-mono" />
      </div>
    </label>
  );
}

function NudgeBtn({ onClick, children, testid }) {
  return (
    <button onClick={onClick} data-testid={testid}
      className="w-7 h-7 grid place-items-center rounded-md bg-secondary hover:bg-primary/10 active:scale-90 transition-transform">
      {children}
    </button>
  );
}
